import * as THREE from "three";
import RAPIER from "@dimforge/rapier3d-compat";

import { SceneManager } from "../core/scene_manager.js";
import { PhysicsWorld } from "../physics/physics_world.js";
import { ResourceManager } from "../resource_loader/resource_manager.js";
import { ResourceID } from "../resource_loader/resource_id.js";
import { SimulationContext } from "../simulation/simulation_context.js";
import { GraphicsSettings } from "../graphics/graphics_quality.js";

/**
 * Art for a themed ground instead of the level's field: the same layout, the
 * floor (`bottom`, its centre spot where the dirt circle is) and the scenery
 * behind it (`top`, transparent above), plus `sky` drawn behind the scenery in
 * place of the scene's gradient sky. No clouds or air balloon.
 */
export interface GroundArt
{
  bottom: THREE.Texture;
  top: THREE.Texture;
  sky: THREE.Texture;
}

export class Ground
{
  private static readonly DIRT_CENTER_Y_IN_BOTTOM_TEXTURE = 0.577;
  private static readonly DIRT_CENTER_HEIGHT_PER_WIDTH = 0.4808;
  private static readonly OLD_ART_PX_W = 1800;
  private static readonly OLD_TOP_PX_H = 416;
  private static readonly OLD_BOTTOM_PX_H = 1847;
  private static readonly COMPOSITION_DOWN_SHIFT = 0.04;
  private static readonly CASTLE_WIDTH_SCALE = 3.2656;
  private static readonly CASTLE_SEAM_OVERLAP = 0.03;
  private static readonly BACKDROP_DISTANCE = 100;
  private static readonly CLOUD_WIDTH = 0.25;
  private static readonly CLOUD_Y = 0.3;
  private static readonly CLOUD_Y_SPREAD = 0.16;
  private static readonly BALLOON_Y = 0.38;
  private static readonly BALLOON_Y_STEP = 0.03;
  private static readonly BACKDROP_CLEARANCE = 1.5;
  private static readonly SHOW_FLOOR_GRID = false;
  private static readonly FLOOR_HALF_SIZE = 100;
  private static readonly FLOOR_HALF_THICKNESS = 0.5;
  /** The sky's lower edge sits this far (share of the scenery height) under the scenery's top, where it is opaque. */
  private static readonly SKY_TUCK = 0.3;

  private readonly clouds: Array<{
    mesh: THREE.Mesh;
    x: number;
    y: number;
    width: number;
    speed: number;
    distanceScale: number;
  }> = [];
  private cloud_right = new THREE.Vector3( 1, 0, 0 );
  private cloud_up = new THREE.Vector3( 0, 1, 0 );
  private cloud_wrap_width = 0;
  private cloud_visible_h = 0;
  private balloon?: {
    mesh: THREE.Mesh;
    x: number;
    y: number;
    width: number;
    baseSpeed: number;
    speed: number;
    targetSpeed: number;
    speedChangeTimer: number;
    baseY: number;
    deviationStep: number;
    currentLevel: number;
    targetLevel: number;
    decisionTimer: number;
    hiddenTimer: number;
  };

  private readonly meshes: THREE.Mesh[] = [];
  private readonly backdrop = new THREE.Group();
  private floor_grid: THREE.LineSegments | null = null;
  private ground_body?: RAPIER.RigidBody;

  constructor( table_ground_position: THREE.Vector3, is_challenge: boolean = false, dirt_screen_y?: number, art?: GroundArt )
  {
    if ( !SimulationContext.enabled )
    {
      SceneManager.world.add( this.backdrop );

      if ( Ground.SHOW_FLOOR_GRID )
        this.createFloorGrid();
      else
      {
        if ( !art )
        {
          this.createClouds();
          this.createAirBalloon();
        }
        this.createImage( table_ground_position, is_challenge, dirt_screen_y, art );
      }

      this.createShadowReceiver();
    }

    this.createCollider();
  }

  private createFloorGrid(): void
  {
    const size = Ground.FLOOR_HALF_SIZE * 2;

    const fill = new THREE.Mesh(
      new THREE.PlaneGeometry( size, size ),
      new THREE.MeshBasicMaterial( { color: 0x4d8a4f, depthTest: false, depthWrite: false } )
    );
    fill.rotation.x = -Math.PI / 2;
    fill.renderOrder = -1003;
    SceneManager.world.add( fill );
    this.meshes.push( fill );

    const half = Ground.FLOOR_HALF_SIZE;
    const positions: number[] = [];
    const colors: number[] = [];
    const color = new THREE.Color();

    const tint = ( at: number ): THREE.Color =>
      color.setHex( at === 0 ? 0xff4040 : at % 5 === 0 ? 0xf2fff2 : 0x2e5e31 );

    const line = ( x0: number, z0: number, x1: number, z1: number, c: THREE.Color ): void =>
    {
      positions.push( x0, 0, z0, x1, 0, z1 );
      colors.push( c.r, c.g, c.b, c.r, c.g, c.b );
    };

    for ( let i = -half; i <= half; i++ )
    {
      line( -half, i, half, i, tint( i ) );

      for ( let z = -half; z < half; z++ )
        line( i, z, i, z + 1, tint( i ) );
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute( "position", new THREE.Float32BufferAttribute( positions, 3 ) );
    geometry.setAttribute( "color", new THREE.Float32BufferAttribute( colors, 3 ) );

    const grid = new THREE.LineSegments(
      geometry,
      new THREE.LineBasicMaterial( { vertexColors: true, depthTest: false, depthWrite: false } )
    );
    grid.renderOrder = -1002;
    grid.frustumCulled = false;

    SceneManager.world.add( grid );
    this.floor_grid = grid;
  }

  // Every pixel of a shadow receiver samples the shadow map, so the catcher is
  // kept just large enough for the table's shadow plus scattered debris. A
  // full-floor plane would charge that cost across most of the screen for
  // ground that never darkens.
  private static readonly SHADOW_CATCHER_SIZE = 14;

  private shadow_catcher: THREE.Mesh | null = null;

  private createShadowReceiver(): void
  {
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry( 1, 1 ),
      new THREE.ShadowMaterial( {
        color: 0x10200d,
        opacity: 0.28,
        depthWrite: false,
      } )
    );

    mesh.rotation.x = -Math.PI / 2;
    mesh.position.y = 0.002;
    mesh.scale.set( Ground.SHADOW_CATCHER_SIZE, Ground.SHADOW_CATCHER_SIZE, 1 );
    mesh.receiveShadow = true;
    SceneManager.world.add( mesh );
    this.meshes.push( mesh );
    this.shadow_catcher = mesh;
  }

  public followCamera( home_eye: THREE.Vector3, farthest_depth: number ): void
  {
    const depth_scale = Math.max( 1, farthest_depth * Ground.BACKDROP_CLEARANCE / Ground.BACKDROP_DISTANCE );

    this.backdrop.scale.setScalar( depth_scale );
    this.backdrop.position.copy( SceneManager.camera.position ).addScaledVector( home_eye, -depth_scale );
    this.backdrop.updateMatrixWorld( true );
  }

  // Nothing outside the light's projection can ever darken, so the receiver is
  // clipped to that footprint: every pixel it drops is nine shadow-map taps
  // saved for ground that was going to stay lit anyway.
  public setShadowArea( min_x: number, max_x: number, min_z: number, max_z: number ): void
  {
    const mesh = this.shadow_catcher;
    if ( !mesh )
      return;

    const size_x = Math.max( 1, max_x - min_x );
    const size_z = Math.max( 1, max_z - min_z );

    mesh.scale.set( size_x, size_z, 1 );
    mesh.position.set( ( min_x + max_x ) * 0.5, 0.002, ( min_z + max_z ) * 0.5 );
    mesh.updateMatrixWorld( true );
  }

  private createClouds(): void
  {
    const texture = ResourceManager.getTexture( ResourceID.CloudTexture );
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.flipY = true;
    texture.needsUpdate = true;

    const camera = SceneManager.camera;
    const distance = 101;
    const visible_h = 2 * Math.tan( THREE.MathUtils.degToRad( camera.fov / 2 ) ) * distance;
    const visible_w = visible_h * camera.aspect;
    const image_aspect = 545 / 351;
    const direction = camera.getWorldDirection( new THREE.Vector3() );
    const right = new THREE.Vector3( 1, 0, 0 ).applyQuaternion( camera.quaternion );
    const up = new THREE.Vector3( 0, 1, 0 ).applyQuaternion( camera.quaternion );
    this.cloud_right.copy( right );
    this.cloud_up.copy( up );
    this.cloud_wrap_width = visible_w;
    this.cloud_visible_h = visible_h;

    // Same idea as MenuScreen: clouds with randomized scale, position
    // and drift speed. Only their parts inside the transparent sky are seen.
    const cloudCount = 5;
    const height_slots = Array.from( { length: cloudCount }, ( _, i ) => i );
    for ( let i = height_slots.length - 1; i > 0; i-- )
    {
      const j = Math.floor( Math.random() * ( i + 1 ) );
      [ height_slots[i], height_slots[j] ] = [ height_slots[j], height_slots[i] ];
    }

    for ( let i = 0; i < cloudCount; i++ )
    {
      const proximity = 0.15 + Math.random() * 0.85;
      const cloudDistance = THREE.MathUtils.lerp( 118, 101, proximity );
      const distanceScale = cloudDistance / distance;
      const scale = 0.75 + Math.random() * 0.5;
      const depthSize = THREE.MathUtils.lerp( 0.62, 1, proximity );
      const finalSizeFactor = scale * depthSize;
      const width = visible_w * Ground.CLOUD_WIDTH * finalSizeFactor;
      const height = width / image_aspect;
      // Distribute clouds across loose horizontal slots. The jitter keeps the
      // result organic, while preventing an empty sky or one large initial pile.
      const slotPosition = ( i + 0.2 + Math.random() * 0.6 ) / cloudCount;
      const x = THREE.MathUtils.lerp(
        -visible_w * 0.5 - width * 0.5,
        visible_w * 0.5,
        slotPosition
      );
      const y = this.cloudY( ( height_slots[i] + Math.random() ) / cloudCount );
      const mesh = new THREE.Mesh(
        new THREE.PlaneGeometry(
          width * distanceScale,
          height * distanceScale
        ),
        new THREE.MeshBasicMaterial( {
          map: texture,
          transparent: true,
          depthWrite: false,
          fog: false
        } )
      );

      mesh.position.copy( camera.position );
      mesh.position.addScaledVector( direction, cloudDistance );
      mesh.position.addScaledVector( right, x * distanceScale );
      mesh.position.addScaledVector( up, y * distanceScale );
      mesh.quaternion.copy( camera.quaternion );
      mesh.renderOrder = -1100;
      this.backdrop.add( mesh );
      this.meshes.push( mesh );

      // Speed follows the final visible size exactly: small clouds are always
      // slower, large clouds are always faster, regardless of random scaling.
      const speedBySize = THREE.MathUtils.clamp(
        ( finalSizeFactor - 0.465 ) / ( 1.25 - 0.465 ),
        0,
        1
      );
      const speed = visible_w * THREE.MathUtils.lerp( 0.0055, 0.01, speedBySize );
      this.clouds.push( { mesh, x, y, width, speed, distanceScale } );
    }
  }

  private cloudY( band_position: number ): number
  {
    return this.cloud_visible_h * ( Ground.CLOUD_Y + Ground.CLOUD_Y_SPREAD * band_position );
  }

  private createAirBalloon(): void
  {
    const texture = ResourceManager.getTexture( ResourceID.AirBalloonTexture );
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.flipY = true;
    texture.needsUpdate = true;

    const camera = SceneManager.camera;
    // Between the clouds (101) and the main artwork (100): the balloon stays
    // in front of clouds but naturally passes behind the Ferris wheel/trees.
    const distance = 100.5;
    const visible_h = 2 * Math.tan( THREE.MathUtils.degToRad( camera.fov / 2 ) ) * distance;
    const visible_w = visible_h * camera.aspect;
    const direction = camera.getWorldDirection( new THREE.Vector3() );
    const right = new THREE.Vector3( 1, 0, 0 ).applyQuaternion( camera.quaternion );
    const up = new THREE.Vector3( 0, 1, 0 ).applyQuaternion( camera.quaternion );
    const width = visible_w * 0.075;
    const height = width * ( 195 / 140 );
    // Start inside the visible upper sky so the player sees the balloon
    // immediately instead of waiting for it to enter from off-screen.
    const x = visible_w * THREE.MathUtils.lerp( -0.22, -0.05, Math.random() );
    // Keep the whole balloon above the sky/fairground boundary even at lane -2,
    // while lane +2 still remains inside the top edge of the viewport.
    const baseY = visible_h * Ground.BALLOON_Y;
    const deviationStep = visible_h * Ground.BALLOON_Y_STEP;
    const initialLevel = THREE.MathUtils.randInt( -2, 2 );
    const y = baseY + initialLevel * deviationStep;

    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry( width, height ),
      new THREE.MeshBasicMaterial( {
        map: texture,
        transparent: true,
        depthWrite: false,
        fog: false
      } )
    );
    mesh.position.copy( camera.position );
    mesh.position.addScaledVector( direction, distance );
    mesh.position.addScaledVector( right, x );
    mesh.position.addScaledVector( up, y );
    mesh.quaternion.copy( camera.quaternion );
    mesh.renderOrder = -1050;
    this.backdrop.add( mesh );
    this.meshes.push( mesh );

    const baseSpeed = visible_w * 0.004;
    this.balloon = {
      mesh,
      x,
      y,
      width,
      baseSpeed,
      speed: baseSpeed,
      targetSpeed: baseSpeed,
      speedChangeTimer: 4 + Math.random() * 5,
      baseY,
      deviationStep,
      currentLevel: initialLevel,
      targetLevel: initialLevel,
      decisionTimer: 6 + Math.random() * 4,
      hiddenTimer: 0,
    };
  }

  public update( dt: number ): void
  {
    if ( !GraphicsSettings.profile().sky_animation )
      return;

    const safe_dt = Math.min( dt, 0.05 );

    for ( const cloud of this.clouds )
    {
      const movement = cloud.speed * safe_dt;
      cloud.x += movement;
      cloud.mesh.position.addScaledVector(
        this.cloud_right,
        movement * cloud.distanceScale
      );

      if ( cloud.x > this.cloud_wrap_width * 0.5 + cloud.width * 0.5 )
      {
        const wrapped_x = -this.cloud_wrap_width * 0.5 - cloud.width * 0.5;
        const wrapped_y = this.cloudY( Math.random() );
        cloud.mesh.position.addScaledVector(
          this.cloud_right,
          ( wrapped_x - cloud.x ) * cloud.distanceScale
        );
        cloud.mesh.position.addScaledVector(
          this.cloud_up,
          ( wrapped_y - cloud.y ) * cloud.distanceScale
        );
        cloud.x = wrapped_x;
        cloud.y = wrapped_y;
      }
    }

    if ( this.balloon )
    {
      const balloon = this.balloon;

      if ( balloon.hiddenTimer > 0 )
      {
        balloon.hiddenTimer -= safe_dt;
        if ( balloon.hiddenTimer <= 0 )
        {
          const spawn_x = -this.cloud_wrap_width * 0.5 - balloon.width * 0.5;
          balloon.mesh.position.addScaledVector( this.cloud_right, spawn_x - balloon.x );
          balloon.x = spawn_x;
          balloon.currentLevel = THREE.MathUtils.randInt( -2, 2 );
          balloon.targetLevel = balloon.currentLevel;
          const spawn_y = balloon.baseY + balloon.currentLevel * balloon.deviationStep;
          balloon.mesh.position.addScaledVector( this.cloud_up, spawn_y - balloon.y );
          balloon.y = spawn_y;
          balloon.decisionTimer = 6 + Math.random() * 4;
          balloon.mesh.visible = true;
        }
        return;
      }

      balloon.speedChangeTimer -= safe_dt;
      if ( balloon.speedChangeTimer <= 0 )
      {
        balloon.targetSpeed = balloon.baseSpeed * THREE.MathUtils.lerp(
          0.75,
          1.5,
          Math.random()
        );
        balloon.speedChangeTimer = 4 + Math.random() * 5;
      }

      // Smooth acceleration/deceleration, bounded by 0.75x..1.5x base speed.
      balloon.speed = THREE.MathUtils.lerp(
        balloon.speed,
        balloon.targetSpeed,
        Math.min( 1, safe_dt * 0.45 )
      );

      const movement = balloon.speed * safe_dt;
      balloon.x += movement;
      balloon.mesh.position.addScaledVector( this.cloud_right, movement );

      if ( balloon.x > this.cloud_wrap_width * 0.5 + balloon.width * 0.5 )
      {
        balloon.mesh.visible = false;
        balloon.hiddenTimer = 2.5 + Math.random() * 3.5;
        return;
      }

      balloon.decisionTimer -= safe_dt;
      if ( balloon.decisionTimer <= 0 )
      {
        const min_step = balloon.targetLevel <= -2 ? 0 : -1;
        const max_step = balloon.targetLevel >= 2 ? 0 : 1;
        balloon.targetLevel += THREE.MathUtils.randInt( min_step, max_step );
        balloon.decisionTimer = 6 + Math.random() * 4;
      }

      // Smoothly approach the chosen discrete lane instead of snapping.
      balloon.currentLevel = THREE.MathUtils.lerp(
        balloon.currentLevel,
        balloon.targetLevel,
        Math.min( 1, safe_dt * 0.35 )
      );
      const next_y = balloon.baseY + balloon.currentLevel * balloon.deviationStep;
      balloon.mesh.position.addScaledVector( this.cloud_up, next_y - balloon.y );
      balloon.y = next_y;
    }
  }

  private static aspectOf( texture: THREE.Texture, fallback: number ): number
  {
    const image = texture.image as { width?: number; height?: number } | undefined;
    return image?.width && image.height
      ? image.width / image.height
      : fallback;
  }

  private static bottomLayout( anchor: THREE.Vector3, bottom_aspect: number, dirt_screen_y?: number )
  {
    const camera = SceneManager.camera;

    const visible_h =
      2 * Math.tan( THREE.MathUtils.degToRad( camera.fov / 2 ) ) * Ground.BACKDROP_DISTANCE;
    const visible_w = visible_h * camera.aspect;

    let width = visible_w;
    let frame_top_height = width * ( Ground.OLD_TOP_PX_H / Ground.OLD_ART_PX_W );
    let frame_bottom_height = width * ( Ground.OLD_BOTTOM_PX_H / Ground.OLD_ART_PX_W );

    const total_height = frame_top_height + frame_bottom_height;

    if ( total_height < visible_h )
    {
      const k = visible_h / total_height;
      width *= k;
      frame_top_height *= k;
      frame_bottom_height *= k;
    }

    const ground_screen_y = Ground.projectWorldYToScreen( anchor );
    const ground_offset = ( 0.5 - ground_screen_y ) * visible_h;

    let frame_bottom_center =
      ground_offset - frame_bottom_height * ( 0.5 - Ground.DIRT_CENTER_Y_IN_BOTTOM_TEXTURE );

    const min_center = -visible_h * 0.5 + frame_bottom_height * 0.5;
    const max_center = visible_h * 0.5 - frame_top_height - frame_bottom_height * 0.5;

    frame_bottom_center = THREE.MathUtils.clamp(
      frame_bottom_center,
      Math.min( min_center, max_center ),
      Math.max( min_center, max_center )
    );

    // Move both background pieces toward the player as one composition. The
    // castle part is positioned from bottom_center below, so their seam stays joined.
    frame_bottom_center -= visible_h * Ground.COMPOSITION_DOWN_SHIFT;

    const bottom_height = width / bottom_aspect;
    const bottom_center = dirt_screen_y === undefined
      ? frame_bottom_center - ( frame_bottom_height - bottom_height ) * 0.5
      : ( 0.5 - dirt_screen_y ) * visible_h - width * Ground.DIRT_CENTER_HEIGHT_PER_WIDTH + bottom_height * 0.5;

    return { visible_h, visible_w, width, frame_top_height, bottom_height, bottom_center };
  }

  private createImage( table_ground_position: THREE.Vector3, is_challenge: boolean, dirt_screen_y?: number, art?: GroundArt ): void
  {
    const camera = SceneManager.camera;

    const castle_texture = art?.top ?? ResourceManager.getTexture(
      is_challenge ? ResourceID.ChallengeTopTexture : ResourceID.BackgroundCastleTexture
    );
    const bottom_texture = art?.bottom ?? ResourceManager.getTexture(
      is_challenge ? ResourceID.ChallengeBackgroundTexture : ResourceID.BackgroundTexture
    );

    const castle_aspect = Ground.aspectOf( castle_texture, camera.aspect );
    const bottom_aspect = Ground.aspectOf( bottom_texture, camera.aspect );

    const {
      visible_h,
      visible_w,
      width,
      frame_top_height,
      bottom_height,
      bottom_center,
    } = Ground.bottomLayout( table_ground_position, bottom_aspect, dirt_screen_y );
    const bottom_top_edge = bottom_center + bottom_height * 0.5;

    let castle_width = frame_top_height * Ground.CASTLE_WIDTH_SCALE;
    let castle_height = castle_width / castle_aspect;
    const min_castle_width = visible_w * 1.06;

    if ( castle_width < min_castle_width )
    {
      castle_height *= min_castle_width / castle_width;
      castle_width = min_castle_width;
    }

    const castle_bottom = bottom_top_edge - visible_h * Ground.CASTLE_SEAM_OVERLAP;
    const castle_center = castle_bottom + castle_height * 0.5;

    const fade_fraction = ( visible_h * Ground.CASTLE_SEAM_OVERLAP ) / castle_height;

    this.createBackgroundPart( bottom_texture, width, bottom_height, bottom_center, -1001, false );
    this.createBackgroundPart( castle_texture, castle_width, castle_height, castle_center, -1000, true, this.createBottomFadeAlphaMap( fade_fraction ) );

    if ( art )
      this.createSky( art.sky, castle_width, castle_center + castle_height * ( 0.5 - Ground.SKY_TUCK ), visible_h );
  }

  /**
   * The sky picture fills everything above the scenery: its lower edge is
   * tucked behind the opaque part of the scenery, and it is scaled up (cropped
   * at the sides) until it reaches the top of the screen, so tall phones see
   * more of it rather than a gap.
   */
  private createSky( texture: THREE.Texture, min_width: number, bottom: number, visible_h: number ): void
  {
    const aspect = Ground.aspectOf( texture, 1.2 );
    const reach = visible_h * 0.5 - bottom;
    const width = Math.max( min_width, reach * aspect );
    const height = width / aspect;

    this.createBackgroundPart( texture, width, height, bottom + height * 0.5, -1002, false );
  }

  private bottom_fade_texture: THREE.CanvasTexture | null = null;

  private createBottomFadeAlphaMap( fade_fraction: number ): THREE.CanvasTexture
  {
    const canvas = document.createElement( "canvas" );
    canvas.width = 1;
    canvas.height = 512;

    const ctx = canvas.getContext( "2d" )!;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect( 0, 0, 1, 512 );

    const fade_rows = Math.max( 2, Math.round( 512 * fade_fraction ) );
    const gradient = ctx.createLinearGradient( 0, 512 - fade_rows, 0, 512 );
    gradient.addColorStop( 0, "#ffffff" );
    gradient.addColorStop( 1, "#000000" );
    ctx.fillStyle = gradient;
    ctx.fillRect( 0, 512 - fade_rows, 1, fade_rows );

    const texture = new THREE.CanvasTexture( canvas );
    this.bottom_fade_texture = texture;

    return texture;
  }

  private createBackgroundPart(
    texture: THREE.Texture,
    width: number,
    height: number,
    center_y: number,
    render_order: number,
    has_alpha: boolean,
    alpha_map?: THREE.Texture
  ): THREE.Mesh
  {
    const camera = SceneManager.camera;
    const distance = Ground.BACKDROP_DISTANCE;

    // Re-uploaded only when the upload would differ: these are 1-4 MP images,
    // and an unconditional needsUpdate sent them to the GPU again on every
    // level and every Prize Shot visit. repeat/offset are uniforms, not upload.
    const needs_upload = texture.version === 0
      || texture.colorSpace !== THREE.SRGBColorSpace
      || texture.flipY !== true;
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.flipY = true;
    texture.repeat.set( 1, 1 );
    texture.offset.set( 0, 0 );
    if ( needs_upload )
      texture.needsUpdate = true;

    const direction = camera.getWorldDirection( new THREE.Vector3() );
    const up = new THREE.Vector3( 0, 1, 0 ).applyQuaternion( camera.quaternion );

    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry( width, height ),
      new THREE.MeshBasicMaterial( {
        map: texture,
        transparent: has_alpha || alpha_map !== undefined,
        alphaMap: alpha_map ?? null,
        depthWrite: false,
        fog: false
      } )
    );

    mesh.position.copy( camera.position );
    mesh.position.addScaledVector( direction, distance );
    mesh.position.addScaledVector( up, center_y );
    mesh.quaternion.copy( camera.quaternion );
    mesh.renderOrder = render_order;

    this.backdrop.add( mesh );
    this.meshes.push( mesh );

    return mesh;
  }

  private static projectWorldYToScreen( world_position: THREE.Vector3 ): number
  {
    const camera = SceneManager.camera;
    camera.updateMatrixWorld( true );
    const projected = world_position.clone().project( camera );
    return ( 1 - projected.y ) * 0.5;
  }

  private createCollider(): void
  {
    this.ground_body = PhysicsWorld.getWorld().createRigidBody(
      RAPIER.RigidBodyDesc.fixed()
        .setTranslation( 0, -Ground.FLOOR_HALF_THICKNESS, 0 )
    );

    PhysicsWorld.getWorld().createCollider(
      RAPIER.ColliderDesc.cuboid( Ground.FLOOR_HALF_SIZE, Ground.FLOOR_HALF_THICKNESS, Ground.FLOOR_HALF_SIZE )
        .setFriction( 1 ),
      this.ground_body
    );
  }

  public dispose(): void
  {
    for ( const mesh of this.meshes )
    {
      mesh.removeFromParent();
      mesh.geometry.dispose();

      const materials = Array.isArray( mesh.material )
        ? mesh.material
        : [mesh.material];

      for ( const material of materials )
        material.dispose();
    }

    this.meshes.length = 0;
    this.backdrop.removeFromParent();

    if ( this.floor_grid )
    {
      this.floor_grid.removeFromParent();
      this.floor_grid.geometry.dispose();
      ( this.floor_grid.material as THREE.Material ).dispose();
      this.floor_grid = null;
    }

    this.bottom_fade_texture?.dispose();
    this.bottom_fade_texture = null;

    if ( this.ground_body )
    {
      PhysicsWorld.getWorld().removeRigidBody( this.ground_body );
      this.ground_body = undefined;
    }
  }
}
